/**
 * Fixed banner displayed at the top of the viewport during demo mode.
 * z-index 110 — always above the Navbar (z-index 100).
 *
 * Expanded (default): full 40px bar with message, collapse button, quit button.
 * Collapsed: 6px coloured bar with a small expand handle on the right edge.
 *
 * The parent (`TourDemoRoute`) must listen to `onHeightChange` and adjust
 * the Tracker wrapper's `paddingTop` accordingly.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TourDemoBanner.module.css';

export const BANNER_HEIGHT_EXPANDED = 40;
export const BANNER_HEIGHT_COLLAPSED = 6;

interface Props {
  /** Called when the user clicks "Quitter la démo". Should clean up mock state. */
  onQuit: () => void;
  /** Called whenever the banner height changes (expanded ↔ collapsed). */
  onHeightChange?: (height: number) => void;
}

/**
 * Collapsible demo mode banner.
 * @param onQuit - callback that cleans up the demo session
 * @param onHeightChange - notifies parent of height changes for paddingTop adjustment
 */
export default function TourDemoBanner({ onQuit, onHeightChange }: Props) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Notify parent on mount and on every collapse/expand
  useEffect(() => {
    onHeightChange?.(isCollapsed ? BANNER_HEIGHT_COLLAPSED : BANNER_HEIGHT_EXPANDED);
  }, [isCollapsed, onHeightChange]);

  const handleQuit = () => {
    onQuit();
    navigate('/login');
  };

  const toggleCollapse = () => setIsCollapsed((v) => !v);

  return (
    <div className={styles.banner} role="alert" aria-live="polite">
      {isCollapsed ? (
        <div className={styles.bannerCollapsed}>
          <button
            className={styles.expandHandle}
            onClick={toggleCollapse}
            title="Afficher la bannière démo"
            aria-label="Afficher la bannière démo"
          >
            ▼
          </button>
        </div>
      ) : (
        <div className={styles.bannerExpanded}>
          <span className={styles.message}>
            ⚠️ MODE DÉMONSTRATION — Données fictives. Aucun vol réel n'est affiché.
          </span>
          <button className={styles.collapseBtn} onClick={toggleCollapse} title="Réduire la bannière">
            ▲ Réduire
          </button>
          <button className={styles.quitBtn} onClick={handleQuit}>
            Quitter la démo
          </button>
        </div>
      )}
    </div>
  );
}
